import { LeadFormAllowedRouteKeys } from '~/constants/lead-forms.consts';
import { BaseHttpAdapter } from '~/services/adapters/base-http.adapter';
import { IApiService } from '~/services/interfaces/IApiService';
import { CXNoAuthApiService } from '~/components/ClientExperience/services/cx-no-auth-api.service';
import {
    IFlowMinimalResponse,
    IFlowResponse,
    IFlowVariablesResponse,
    IPublicFlowSubmitResponse,
    IRecurringInvoiceResponse,
    ISubmitLeadFormTaskResponse,
    ILeadFormCxExperienceResponse
} from '~/types/server.types';
import { IAuthResponse } from '~/components/ClientExperience/types/server.types';
import { CXFlow } from '~/components/ClientExperience/OneFlow/models/cx-flow.model';
import { CXEnvironments } from '~/types/cx-types/cx-environment.types';
import { ClientPortalConfigStore } from '~/components/ClientExperience/stores/client-portal-config.store';
import { S3ApiAdapter } from '~/components/ClientExperience/services/adapters/s3-api.adapter';
import { getStagingNameFromUrl } from '~/utils/url.utils';
import { TLeadFormSubmitSchedulerData } from '~/components/ClientExperience/OneFlow/components/FlowView/LeadFormSubmit/LeadFormSubmit.types';
import { CaptureThumbnailParams } from '~/components/ClientExperience/components/PublicTemplateThumbnails/types';

export interface IFlowSubmitOptions {
    force?: boolean;
}

export const flowSubmitOptionsDefault = { force: false };

export type FlowCXAdapterProps = {
    apiAdapter: IApiService;
    cxNoAuthApiService: CXNoAuthApiService;
    clientPortalConfigStore: ClientPortalConfigStore;
    s3ApiAdapter: S3ApiAdapter;
};

export class FlowCXAdapter extends BaseHttpAdapter {
    protected cxNoAuthApiService: CXNoAuthApiService;
    protected clientPortalConfigStore: ClientPortalConfigStore;
    protected s3ApiAdapter: S3ApiAdapter;

    constructor({
        apiAdapter,
        cxNoAuthApiService,
        s3ApiAdapter,
        clientPortalConfigStore
    }: FlowCXAdapterProps) {
        super(apiAdapter);

        this.cxNoAuthApiService = cxNoAuthApiService;
        this.s3ApiAdapter = s3ApiAdapter;
        this.clientPortalConfigStore = clientPortalConfigStore;
    }
    async fetchFlowMinimalData(
        flowId: string,
        userId: string
    ): Promise<IFlowMinimalResponse> {
        return this.cxNoAuthApiService.apiRequest<IFlowMinimalResponse>({
            method: 'get',
            url: `/api/v2/flow/${flowId}/minimal`,
            params: {
                user_id: userId
            }
        });
    }

    _fetchFlow(flowId: string): Promise<IFlowResponse> {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'get',
            url: `/api/v2/flow/${flowId}/active`
        });
    }

    _fetchViewOnlyFlow(flowId: string): Promise<IFlowResponse> {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'get',
            url: `/api/v2/flow/${flowId}/view`
        });
    }

    async fetchActivePublicFlow(
        flowId: string,
        companyId: string
    ): Promise<IFlowResponse> {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'get',
            url: `/api/v2/public_flow/${flowId}/active`,
            leadFormRouteKey: LeadFormAllowedRouteKeys.FetchFlow,
            params: { company_id: companyId }
        });
    }

    async fetchActiveLeadForm(
        flowId: string,
        companyId: string
    ): Promise<IFlowResponse> {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'get',
            url: `/api/v2/public_flow/${flowId}/active`,
            leadFormRouteKey: LeadFormAllowedRouteKeys.FetchFlow,
            params: { company_id: companyId }
        });
    }

    async checkFlowAuth(flowId: string, fToken: string) {
        return this.apiService.apiRequest<IAuthResponse>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/auth`,
            data: { fToken }
        });
    }

    // TODO: type data
    async submitFlow(
        flowId: string,
        revisionId: string,
        pageId: string,
        transformedData: { variable_id: string; value: any }[],
        { force }: IFlowSubmitOptions = flowSubmitOptionsDefault
    ) {
        try {
            const filteredData = transformedData.filter(
                variable => variable.value?.raw_data
            );

            if (
                this.clientPortalConfigStore.env.environment === 'production' &&
                filteredData.length > 0
            ) {
                await window?.analytics?.track?.(
                    'submitFlow with upload file question block',
                    {
                        revision_id: revisionId,
                        filtered_data: filteredData,
                        force_submit: force,
                        flow_id: flowId,
                        page_id: pageId
                    },
                    {
                        All: false,
                        Mixpanel: true,
                        Statsig: true,
                        'Mixpanel (Actions)': true
                    }
                );
            }
        } catch (e) {
            console.error('Error in submitFlow analytics', e);
        }

        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/flow/${flowId}/submit/${pageId}`,
            data: {
                revision_id: revisionId,
                data: transformedData,
                force_submit: force
            }
        });
    }

    async submitViewOnly(flowId: string, pageId: string) {
        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/flow/${flowId}/submit_view_only/${pageId}`
        });
    }

    async approvePendingChange(flowId: string, blockId: string) {
        return this.apiService.apiRequest<Partial<CXFlow>>({
            method: 'put',
            url: `/api/v2/flow/${flowId}/${blockId}/approve`
        });
    }

    async sign(
        flowId: string,
        revisionId: string,
        pageId: string,
        transformedData: { variable_id: string; value: any }[]
    ) {
        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/flow/${flowId}/sign_contract/${pageId}`,
            data: {
                revision_id: revisionId,
                data: transformedData
            }
        });
    }

    async answerQuestion(
        flowId: string,
        revisionId: string,
        pageId: string,
        transformedData: { variable_id: string; value: any }[]
    ) {
        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/flow/${flowId}/answer_question/${pageId}`,
            data: {
                revision_id: revisionId,
                data: transformedData
            }
        });
    }

    async selectService(
        flowId: string,
        revisionId: string,
        pageId: string,
        transformedData: { variable_id: string; value: any }[]
    ) {
        return this.apiService.apiRequest({
            method: 'put',
            url: `/api/v2/flow/${flowId}/select_service/${pageId}`,
            data: {
                revision_id: revisionId,
                data: transformedData
            }
        });
    }

    validateSubmit({
        flowId,
        revisionId
    }: {
        flowId: string;
        revisionId: string;
    }): Promise<void> {
        return this.apiService.apiRequest({
            method: 'get',
            url: `/api/v2/flow/${flowId}/validate?revision_id=${revisionId}`
        });
    }

    async _getFlowPaymentsPreview(
        flowId: string,
        selectedSsiIds: string[],
        selectedCustomQuantities: { block_id: string; quantity: number }[],
        isActiveRevisionMode: boolean = false,
        numberOfPayments?: number
    ) {
        return this.apiService.apiRequest<IFlowVariablesResponse>({
            method: 'get',
            url: `/api/v2/flow/${flowId}/preview_payments`,
            params: {
                selected_ssi_block_ids: selectedSsiIds,
                selected_custom_quantities: selectedCustomQuantities,
                number_of_payments: numberOfPayments,
                is_active_revision: isActiveRevisionMode
            }
        });
    }

    async getPaymentConnectedInvoice(flowId: string, paymentId: string) {
        return this.apiService.apiRequest<IRecurringInvoiceResponse>({
            method: 'get',
            url: `/api/v2/flow/${flowId}/payment_invoice/${paymentId}`,
            params: {}
        });
    }

    async getLeadFormNavExperience(): Promise<ILeadFormCxExperienceResponse> {
        const companyId = this.clientPortalConfigStore?.clientPortalCompanyId;
        return this.apiService.apiRequest<ILeadFormCxExperienceResponse>({
            method: 'get',
            url: `/api/v2/public/${companyId}/experience`,
            leadFormRouteKey: LeadFormAllowedRouteKeys.FetchLeadFormExperiment
        });
    }

    async fetchPublicFlow(flowId: string, companyId: string) {
        const {
            env: { environment }
        } = this.clientPortalConfigStore;

        const isProd = environment === CXEnvironments.Production;
        const isLocal = environment === CXEnvironments.Development;
        const s3UrlPrefix = isProd
            ? 'https://drz60ich0zu0v.cloudfront.net'
            : 'https://d258zsheehsnq8.cloudfront.net';

        const envName = this.getEnvNameForFlowS3(environment);

        let leadFormUrl = `${s3UrlPrefix}/exports/public_flows/${envName}/${companyId}/${flowId}.json`;
        leadFormUrl = isLocal ? leadFormUrl + '?local=true' : leadFormUrl;

        return this.s3ApiAdapter.apiRequest<IFlowResponse>({
            baseURL: '',
            url: leadFormUrl,
            headers: { crossDomain: true }
        });
    }

    private getEnvNameForFlowS3(environment: CXEnvironments) {
        const {
            configuration: { staging_api_base_url }
        } = this.clientPortalConfigStore;

        switch (environment) {
            case CXEnvironments.Production:
                return 'prod';
            case CXEnvironments.Staging:
                return getStagingNameFromUrl(staging_api_base_url);
            case CXEnvironments.Development:
                if (!process.env.USER) {
                    const message = `ERROR! - process.env.USER is empty - will load public flow from shared "local" folder!`;
                    console.log(
                        '%c' + `🤯${message}`,
                        'color: red; font-size: 20px; font-weight: bold;'
                    );
                }
                return process.env.USER || 'local';
        }
    }

    async fetchSubmitResponse(token: string) {
        return this.apiService.apiRequest<IPublicFlowSubmitResponse>({
            method: 'get',
            url: `/api/v2/public/submit_results`,
            leadFormRouteKey:
                LeadFormAllowedRouteKeys.FetchLeadFormSubmitResults,
            params: {
                token: token
            }
        });
    }

    protected async _submitLeadForm({
        flowId,
        company_id,
        payment_data,
        transformedData,
        email,
        scheduleData,
        recaptchaData,
        revisionId,
        fullName,
        sms_consent_text_version,
        leadPhoneNumber,
        time_zone_offset
    }: {
        flowId: string;
        transformedData: { variable_id: string; value: any }[];
        email: string;
        company_id: string;
        fullName: string;
        revisionId: string;
        scheduleData: TLeadFormSubmitSchedulerData;
        payment_data;
        recaptchaData: object;
        sms_consent_text_version?: number;
        leadPhoneNumber?: string;
        time_zone_offset?: number;
    }) {
        try {
            const filteredData = transformedData.filter(
                variable => variable.value?.raw_data
            );

            if (
                this.clientPortalConfigStore.env.environment === 'production' &&
                filteredData.length > 0
            ) {
                await window?.analytics?.track?.(
                    '_submitLeadForm with upload file question block',
                    {
                        filtered_data: filteredData,
                        email,
                        company_id,
                        full_name: fullName,
                        revision_id: revisionId
                    },
                    {
                        All: false,
                        Mixpanel: true,
                        Statsig: true,
                        'Mixpanel (Actions)': true
                    }
                );
            }
        } catch (e) {
            console.error('Error in _submitLeadForm analytics', e);
        }

        return this.apiService.apiRequest<ISubmitLeadFormTaskResponse>({
            method: 'put',
            url: `/api/v2/public/${flowId}/submit`,
            leadFormRouteKey: LeadFormAllowedRouteKeys.SubmitLeadForm,
            data: {
                data: transformedData,
                email,
                company_id,
                full_name: fullName,
                revision_id: revisionId,
                schedule_data: scheduleData,
                payment_data,
                ...recaptchaData,
                sms_consent_text_version,
                phone_number: leadPhoneNumber,
                time_zone_offset: time_zone_offset
            }
        });
    }

    protected _selectNumberOfPayments({
        flowId,
        numberOfPayments
    }: {
        flowId: string;
        numberOfPayments: number;
    }) {
        return this.apiService.apiRequest<IFlowResponse>({
            method: 'post',
            url: `/api/v2/flow/${flowId}/select_number_of_payments`,
            data: { number_of_payments: numberOfPayments }
        });
    }

    async getPageThumbnailURL({
        template_id,
        ..._params
    }: CaptureThumbnailParams) {
        return this.apiService.apiRequest<{ url: string }>({
            method: 'get',
            url: `/api/v2/flow/${template_id}/template/thumbnail_url`,
            params: _params
        });
    }
}
